'use strict';

/**
 * Yamaha parts lookup (ypicweb / EPC Online integration).
 *
 * Talks to two APIs:
 *   - adminapi.epconline.com.au : returns the dealer access key (CORS-enabled JSON)
 *   - ypicweb.epconline.com.au  : the parts catalogue (no CORS — JSONP only)
 *
 * Markup contract (rendered by the Shopify page content, not this script):
 *   #TypeSelection, #TypeSelect, #YearSelect, #ModelSelect, #ContentSelect,
 *   #AssemblySelect, #AssemblyContainer, #PartsList tbody, #PartsListStatus,
 *   #PartsListContainer, #DiagramWrap > img, #Diagram, #DiagramStatus,
 *   #zoomIn, #zoomOut, #cartTotal, #yamaha, #vin-search-text, #sub-vin-search.
 */
(function () {
  const DEALER_KEY = '76a5f19c-c46d-40cc-ba02-8c919b71565d';
  const PRODUCT_KEY_YAMAHA = '324d31df-d5ab-4449-85a7-43e5a97c3e28';
  const ADMINAPI = 'https://adminapi.epconline.com.au';
  const YPICWEB = 'https://ypicweb.epconline.com.au';
  // Cross-origin call to the bridge PHP. The Yamaha-parts page template renders a
  // short-lived HMAC-signed token into window.YAMAHA_BRIDGE; the PHP verifies it
  // before doing any Shopify Admin API work. Without a valid token the endpoint
  // returns 401.
  const CART_BRIDGE = 'https://secure.hostguys.biz/huntsmarine/yamaha-parts.php';
  const CONFIGURED_PRODUCT_TYPES = ['MA'];
  const PRICE_MARGIN = 1.1; // 10% GST on parts.
  const LOADING_GIF = 'https://cdn.shopify.com/s/files/1/1406/4308/files/loading.gif?v=1661439435';

  const TYPE_LABELS = { MA: { id: '1', label: 'Marine' }, MB: { id: '0', label: 'Motorcycle' } };

  const priceFormatter = new Intl.NumberFormat('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  /** @type {string|null} */
  let dealerId = null;
  /** @type {HTMLImageElement|null} */
  let diagramImage = null;

  document.addEventListener('DOMContentLoaded', boot);
  if (document.readyState !== 'loading') boot();

  let booted = false;
  function boot() {
    if (booted) return;
    // Guard: only run on the Yamaha parts page. The script is template-scoped, but
    // this keeps the access-key fetch from ever firing if the script is loaded
    // somewhere without the lookup markup.
    if (!document.querySelector('#yamaha')) return;
    booted = true;

    // Note: we deliberately do NOT fetch the access key here. It's fetched lazily
    // on first interaction (see ensureDealerId) so that crawlers, prefetchers and
    // casual visitors who never run a lookup don't trigger an adminapi call.
    refreshCartWidget();
    setupTypeSelector();
    bindVinSearch();
    bindZoomControls();
    bindCartHandlers();
    bindPartsRowHighlight();

    const wrap = document.querySelector('#DiagramWrap');
    diagramImage = wrap ? wrap.querySelector('img') : null;
  }

  // ─── HTTP ────────────────────────────────────────────────────────────────────

  /** @type {Promise<string>|null} In-flight/settled key fetch, so we only ever call adminapi once. */
  let dealerIdPromise = null;

  /**
   * Lazily fetch (and cache) the dealer access key. Safe to call repeatedly —
   * the underlying adminapi request happens at most once per page load, on the
   * first parts lookup rather than on page load.
   * @returns {Promise<string>}
   */
  function ensureDealerId() {
    if (dealerId) return Promise.resolve(dealerId);
    if (!dealerIdPromise) {
      dealerIdPromise = fetchAccessKey().then((key) => {
        dealerId = key;
        return key;
      }).catch((err) => {
        dealerIdPromise = null; // allow a retry on the next interaction
        throw err;
      });
    }
    return dealerIdPromise;
  }

  async function fetchAccessKey() {
    const url = `${ADMINAPI}/Subscription/AccessKey/${DEALER_KEY}/${PRODUCT_KEY_YAMAHA}`;
    const res = await fetch(url, { credentials: 'omit' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const key = await res.json(); // adminapi returns a JSON-encoded string
    return String(key).replace(/"/g, '').trim();
  }

  /**
   * Promise-returning JSONP helper. ypicweb has no CORS support but does honour ?callback=.
   * @param {string} url
   * @returns {Promise<*>}
   */
  function jsonp(url) {
    return new Promise((resolve, reject) => {
      const cbName = `__ypicCb_${Date.now()}_${Math.floor(Math.random() * 1e9)}`;
      const script = document.createElement('script');
      const cleanup = () => {
        delete window[cbName];
        script.remove();
      };
      window[cbName] = (data) => { cleanup(); resolve(data); };
      script.onerror = () => { cleanup(); reject(new Error(`JSONP failed: ${url}`)); };
      const sep = url.includes('?') ? '&' : '?';
      script.src = `${url}${sep}callback=${cbName}`;
      document.head.appendChild(script);
    });
  }

  // Every catalogue call funnels through here, so this is the single choke point
  // that guarantees the access key is fetched (once) before it's needed — and not
  // before. dealerId is the access key returned by ensureDealerId().
  const ypic = async (path) => {
    const id = await ensureDealerId();
    return jsonp(`${YPICWEB}${path}/${id}`);
  };

  // ─── Type / Year / Model / Content / Assembly cascade ───────────────────────

  function setupTypeSelector() {
    const typeSelect = /** @type {HTMLSelectElement|null} */(document.querySelector('#TypeSelect'));
    if (!typeSelect) return;

    if (CONFIGURED_PRODUCT_TYPES.length > 1) {
      typeSelect.innerHTML = '';
      typeSelect.appendChild(makeOption('-1', 'Please select a Type'));
      for (const code of CONFIGURED_PRODUCT_TYPES) {
        const t = TYPE_LABELS[code];
        if (t) typeSelect.appendChild(makeOption(t.id, t.label));
      }
      typeSelect.addEventListener('change', () => loadYears(typeSelect.value));
      return;
    }

    // Single configured type: replace the dropdown with a hidden input.
    const code = CONFIGURED_PRODUCT_TYPES[0];
    const typeId = (TYPE_LABELS[code] || TYPE_LABELS.MB).id;

    const hidden = document.createElement('input');
    hidden.id = 'TypeSelect';
    hidden.type = 'hidden';
    hidden.value = typeId;
    typeSelect.replaceWith(hidden);
    document.querySelector('#TypeSelection')?.appendChild(hidden);

    // Populate the year list lazily, on the visitor's first engagement with the
    // Year control, instead of on page load. The cascade (and therefore the
    // access-key fetch in ypic) only runs once someone actually starts a lookup.
    const yearSelect = document.querySelector('#YearSelect');
    if (!yearSelect) {
      // No year control to lazy-bind against — keep the original eager behaviour
      // so functionality isn't silently lost.
      loadYears(typeId);
      return;
    }
    let started = false;
    const startLookup = () => {
      if (started) return;
      started = true;
      loadYears(typeId);
    };
    yearSelect.addEventListener('mousedown', startLookup);
    yearSelect.addEventListener('focus', startLookup);
  }

  async function loadYears(typeId, preselectYear) {
    hideAll(['#AssemblySelect', '#ContentSelect', '#ModelSelect', '#AssemblyContainer']);
    const yearSelect = /** @type {HTMLSelectElement|null} */(document.querySelector('#YearSelect'));
    if (!yearSelect) return;
    setLoading(yearSelect);

    let years;
    try {
      years = await ypic(`/Products/Years/${typeId}`);
    } catch (err) {
      console.error('Yamaha parts: load years failed', err);
      yearSelect.innerHTML = '';
      yearSelect.appendChild(makeOption('-1', 'Failed to load years'));
      return;
    }

    yearSelect.innerHTML = '';
    yearSelect.appendChild(makeOption('-1', 'Please select a year'));
    for (const y of years) {
      const opt = makeOption(String(y), String(y));
      opt.id = `ModelYear_${y}`;
      if (preselectYear !== undefined && String(y) === String(preselectYear)) opt.selected = true;
      yearSelect.appendChild(opt);
    }

    // Reset and re-bind so a previously-selected handler doesn't fire with stale closure values.
    yearSelect.onchange = () => loadModels(typeId, yearSelect.value);
  }

  async function loadModels(typeId, year, preselectProductId) {
    hideAll(['#AssemblySelect', '#ContentSelect', '#AssemblyContainer']);
    const modelSelect = /** @type {HTMLSelectElement|null} */(document.querySelector('#ModelSelect'));
    if (!modelSelect) return;
    setLoading(modelSelect);
    show(modelSelect);

    let models;
    try {
      models = await ypic(`/Products/${typeId}/Year/${year}`);
    } catch (err) {
      console.error('Yamaha parts: load models failed', err);
      modelSelect.innerHTML = '';
      modelSelect.appendChild(makeOption('-1', 'Failed to load models'));
      return;
    }

    modelSelect.innerHTML = '';
    modelSelect.appendChild(makeOption('-1', 'Please select a Model'));
    for (const item of models) {
      const opt = makeOption('', item.Model);
      opt.id = `model_${item.ProductID}`;
      opt.dataset.prodid = String(item.ProductID);
      // Compare as strings — ProductID may be a number from the API and a string from the data-attr.
      if (preselectProductId !== undefined && String(item.ProductID) === String(preselectProductId)) {
        opt.selected = true;
      }
      modelSelect.appendChild(opt);
    }

    modelSelect.onchange = () => {
      const selected = modelSelect.options[modelSelect.selectedIndex];
      const productId = selected?.dataset.prodid;
      if (productId) loadContent(productId);
    };
  }

  async function loadContent(productId) {
    if (!productId) return;
    hideAll(['#AssemblySelect', '#AssemblyContainer']);
    const contentSelect = /** @type {HTMLSelectElement|null} */(document.querySelector('#ContentSelect'));
    if (!contentSelect) return;
    setLoading(contentSelect);
    show(contentSelect);

    let items;
    try {
      items = await ypic(`/Content/Product/${productId}`);
    } catch (err) {
      console.error('Yamaha parts: load content failed', err);
      return;
    }

    contentSelect.innerHTML = '';
    contentSelect.appendChild(makeOption('-1', 'Please select...'));
    for (const item of items) {
      const opt = makeOption('', item.Title);
      opt.id = `content_${item.ContentID}`;
      opt.dataset.contentid = String(item.ContentID);
      contentSelect.appendChild(opt);
    }

    contentSelect.onchange = () => {
      const selected = contentSelect.options[contentSelect.selectedIndex];
      const contentId = selected?.dataset.contentid;
      if (contentId) loadAssemblies(productId, contentId);
    };
  }

  async function loadAssemblies(productId, contentId) {
    if (!productId || !contentId) return;
    hide('#AssemblyContainer');
    const assemblySelect = /** @type {HTMLSelectElement|null} */(document.querySelector('#AssemblySelect'));
    if (!assemblySelect) return;
    setLoading(assemblySelect);
    show(assemblySelect);

    let items;
    try {
      items = await ypic(`/Assembly/Content/${contentId}`);
    } catch (err) {
      console.error('Yamaha parts: load assemblies failed', err);
      return;
    }

    assemblySelect.innerHTML = '';
    assemblySelect.appendChild(makeOption('-1', 'Please select an assembly'));
    for (const item of items) {
      const opt = makeOption('', item.Title);
      opt.id = `assembly_${item.AssemblyID}`;
      opt.dataset.assid = String(item.AssemblyID);
      assemblySelect.appendChild(opt);
    }

    assemblySelect.onchange = () => {
      show('#AssemblyContainer');
      const selected = assemblySelect.options[assemblySelect.selectedIndex];
      const assemblyId = selected?.dataset.assid;
      if (!assemblyId) return;
      loadAssemblyImage(assemblyId);
      loadPartsForAssembly(productId, assemblyId);
    };
  }

  async function loadPartsForAssembly(productId, assemblyId) {
    if (!productId || !assemblyId) return;

    const status = document.querySelector('#PartsListStatus');
    if (status) {
      status.innerHTML =
        `<img src="${LOADING_GIF}" alt="Loading" class="loading" style="width:32px;height:32px"/>` +
        `<br/>Loading parts list ...`;
      show(status);
    }

    let parts;
    try {
      parts = await ypic(`/Part/Assembly/${productId}/${assemblyId}`);
    } catch (err) {
      console.error('Yamaha parts: load parts failed', err);
      if (status) status.textContent = 'Failed to load parts list.';
      return;
    }

    const tbody = document.querySelector('#PartsList tbody');
    if (!tbody) return;
    tbody.innerHTML = '';

    for (const item of parts) {
      // Superseded parts: show the SsPartNo + SsPrice instead.
      const isSuperseded = item.SsPartNo != null;
      const partNo = isSuperseded ? item.SsPartNo : item.PartNo;
      const price = withMargin(isSuperseded ? item.SsPrice : item.Price);

      const tr = document.createElement('tr');
      tr.id = `part_${item.PartID}`;
      tr.className = 'partrow';
      tr.dataset.partid = String(item.PartID);
      tr.dataset.partno = String(partNo);
      tr.dataset.quantity = String(item.Quantity);
      tr.dataset.orderqty = '1';
      tr.dataset.price = price != null ? String(price) : '';
      tr.dataset.refno = String(item.RefNo);
      tr.dataset.descrp = String(item.Description);

      const partNoTd = `<td${isSuperseded ? ' class="supersession"' : ''}>${escapeHtml(partNo)}</td>`;
      const priceTd = price != null
        ? `<td class="priceCol">$${priceFormatter.format(price)}</td>`
        : `<td class="priceCol"></td>`;

      const actionTd = price != null
        ? `<td><button type="button" class="productContentfooterLeft btnAddToCart"` +
          ` data-add-part data-partid="${escapeAttr(item.PartID)}"` +
          ` data-partno="${escapeAttr(partNo)}">Add to Cart</button></td>`
        : `<td><a href="contact_store.php?desc=${encodeURIComponent(item.Description)}` +
          `&product_id=${encodeURIComponent(item.PartID)}" class="contactstore" rel="facebox">Contact Store</a></td>`;

      tr.innerHTML =
        `<td class="refCol">${escapeHtml(item.RefNo)}</td>` +
        `<td class="text descCol">${escapeHtml(item.Description)}</td>` +
        partNoTd +
        `<td class="numberCol">${escapeHtml(item.Quantity)}</td>` +
        `<td><input id="qty_${escapeAttr(item.PartID)}" name="qty_${escapeAttr(item.PartID)}" type="text" class="qtyTextbox" value="1"/></td>` +
        priceTd +
        actionTd;

      tbody.appendChild(tr);
    }

    if (status) {
      status.textContent = '';
      hide(status);
    }
    show('#PartsListContainer');
  }

  async function loadAssemblyImage(assemblyId) {
    const diagramStatus = document.querySelector('#DiagramStatus');
    if (diagramStatus) {
      diagramStatus.innerHTML =
        `<div style="padding:2rem;">` +
        `<img src="${LOADING_GIF}" alt="Loading" class="loading" style="width:32px;height:32px"/>` +
        `<br/><br/>Loading diagram</div>`;
      show(diagramStatus);
    }

    let data;
    try {
      data = await ypic(`/Assembly/Image/${assemblyId}`);
    } catch (err) {
      console.error('Yamaha parts: load image failed', err);
      if (diagramStatus) diagramStatus.textContent = 'Failed to load diagram.';
      return;
    }

    if (diagramImage && data) {
      diagramImage.src = `${YPICWEB}/Image/getImage/${data.Key}/type/${data.Value}`;
    }
    if (diagramStatus) {
      diagramStatus.textContent = '';
      hide(diagramStatus);
    }
    show('#Diagram');
  }

  // ─── VIN search ─────────────────────────────────────────────────────────────

  function bindVinSearch() {
    const btn = document.querySelector('#sub-vin-search');
    if (!btn) return;
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      const input = /** @type {HTMLInputElement|null} */(document.querySelector('#vin-search-text'));
      const vin = input?.value.trim();
      if (!vin) return;

      let results;
      try {
        results = await ypic(`/Products/Vin/${encodeURIComponent(vin)}`);
      } catch (err) {
        console.error('Yamaha parts: VIN lookup failed', err);
        return;
      }
      if (!results || results.length === 0) {
        alert('Sorry, that VIN could not be found');
        return;
      }

      for (const item of results) {
        const productId = item.ProductID;
        const selectedType = item.Type;
        const year = item.Year;
        setTypeSelector(selectedType);
        await loadYears(selectedType, year);
        await loadModels(selectedType, year, productId);
        await loadContent(productId);
      }
    });
  }

  function setTypeSelector(typeId) {
    const sel = /** @type {HTMLSelectElement|null} */(document.querySelector('#TypeSelect'));
    if (!sel || sel.tagName !== 'SELECT') return;
    for (const opt of Array.from(sel.options)) {
      opt.selected = opt.value === String(typeId);
    }
  }

  // ─── Cart ───────────────────────────────────────────────────────────────────

  function bindCartHandlers() {
    document.addEventListener('click', async (e) => {
      const target = /** @type {HTMLElement} */(e.target);
      const btn = target.closest('[data-add-part]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      const row = btn.closest('tr');
      if (!row) return;

      const partNo = row.dataset.partid;
      const partSku = row.dataset.partno;
      const partQty = row.dataset.quantity;
      const partPrice = Math.round(parseFloat(row.dataset.price || '0') * 100) / 100;
      const partDesc = row.dataset.descrp || '';
      const orderQtyInput = /** @type {HTMLInputElement|null} */(document.querySelector(`#qty_${partNo}`));
      const partOrderQty = orderQtyInput?.value || '1';

      const msg = showCartMessage('Adding to cart, please wait...', e.clientY);

      const token = window.YAMAHA_BRIDGE || {};
      if (!token.n || !token.s) {
        console.error('Yamaha bridge token missing — check theme settings and page template');
        msg.innerHTML = cartErrorHtml('Configuration error');
        return;
      }

      // Step 1: ensure a Shopify product exists for this part (legacy bridge).
      const bridgeUrl = `${CART_BRIDGE}?partNo=${encodeURIComponent(partNo)}` +
        `&partSku=${encodeURIComponent(partSku)}` +
        `&partQty=${encodeURIComponent(partQty)}` +
        `&partOrderQty=${encodeURIComponent(partOrderQty)}` +
        `&partPrice=${encodeURIComponent(partPrice)}` +
        `&partDesc=${encodeURIComponent(partDesc)}` +
        `&n=${encodeURIComponent(token.n)}` +
        `&s=${encodeURIComponent(token.s)}`;

      let variantId;
      try {
        const bridgeRes = await fetch(bridgeUrl, { credentials: 'omit' });
        const bridgeData = await bridgeRes.json().catch(() => ({}));
        if (!bridgeRes.ok) {
          throw new Error(bridgeData.error || `HTTP ${bridgeRes.status}`);
        }
        if (!(typeof bridgeData.id === 'number' && bridgeData.id > 0)) {
          throw new Error(`Invalid variant id: ${bridgeData.id}`);
        }
        variantId = bridgeData.id;
      } catch (err) {
        console.error('Yamaha parts: cart bridge failed', err);
        msg.innerHTML = cartErrorHtml('Could not prepare item');
        return;
      }

      // Step 2: add the resulting variant to the Shopify cart.
      try {
        const cartRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: parseInt(partOrderQty, 10) || 1 }),
        });
        const cartData = await cartRes.json();
        if (cartRes.ok && cartData.id === variantId) {
          msg.innerHTML = cartSuccessHtml(cartData.title);
          refreshCartWidget();
        } else {
          msg.innerHTML = cartErrorHtml(`${cartData.message || ''} ${cartData.description || ''}`.trim());
        }
      } catch (err) {
        console.error('Yamaha parts: cart add failed', err);
        msg.innerHTML = cartErrorHtml('Network error');
      }
    });
  }

  function showCartMessage(text, clientY) {
    document.querySelectorAll('.cartAddMsg').forEach((el) => el.remove());
    const msg = document.createElement('div');
    msg.className = 'cartAddMsg';
    // CSS hides .cartAddMsg by default; the old jQuery used .fadeIn() which forced display:block.
    msg.style.display = 'block';
    msg.style.top = `${(clientY || 0) - 50}px`;
    msg.textContent = text;
    (document.querySelector('#yamaha') || document.body).appendChild(msg);
    return msg;
  }

  function cartSuccessHtml(title) {
    return `<p><strong>${escapeHtml(title)} added to cart</strong></p>` +
      `<p><a href="/cart" class="button" target="_parent" style="float:right;">View Cart</a> ` +
      `<a href="#" class="button" data-close-cart-msg>Close</a></p>`;
  }

  function cartErrorHtml(detail) {
    const base = 'There was an error adding this item to your cart, please contact ' +
      'info@huntsmarine.com.au or call (02) 9546-1324';
    const extra = detail ? `<br/><small>${escapeHtml(detail)}</small>` : '';
    return `<p><strong>There was a problem:</strong> ${base}${extra}</p>` +
      `<p><a href="/cart" class="button" target="_parent" style="float:right;">View Cart</a> ` +
      `<a href="#" class="button" data-close-cart-msg>Close</a></p>`;
  }

  document.addEventListener('click', (e) => {
    const target = /** @type {HTMLElement} */(e.target);
    if (target.closest('[data-close-cart-msg]')) {
      e.preventDefault();
      target.closest('.cartAddMsg')?.remove();
    }
  });

  async function refreshCartWidget() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return;
      const data = await res.json();
      const total = window.Shopify && window.Shopify.formatMoney
        ? window.Shopify.formatMoney(data.total_price)
        : `$${(data.total_price / 100).toFixed(2)}`;
      const itemsWord = data.item_count === 1 ? 'item' : 'items';

      // In-page widget (#cartTotal in templates/page.yamaha-parts.liquid markup).
      const target = document.querySelector('#cartTotal');
      if (target) {
        target.innerHTML =
          `<a href="/cart" class="cart-count" target="_parent">` +
          `<i class="fa fa-shopping-cart"></i> ${data.item_count} ${itemsWord} - ${total}</a> ` +
          `<a href="/cart" class="button" target="_parent" style="margin-left:20px">View Cart</a>`;
      }

      // Global theme header carts — multiple instances (desktop + mobile + sticky etc.)
      // The Liquid in sections/header.liquid renders these as:
      //   <a class="cart-count">
      //     <i class="fa fa-shopping-cart"></i> {{ count }}
      //     <span class="items">items</span>
      //     <span class="price"> - {{ total }}</span>
      //   </a>
      // We have to rebuild rather than just swap a text node — the count sits as a
      // bare text node between the icon and the spans, which is awkward to target.
      document.querySelectorAll('a.cart-count').forEach((el) => {
        if (el.closest('#cartTotal')) return; // already handled above
        el.innerHTML =
          `<i class="fa fa-shopping-cart"></i> ${data.item_count} ` +
          `<span class="items">${itemsWord}</span>` +
          `<span class="price"> - ${total}</span>`;
      });
    } catch (err) {
      console.warn('Yamaha parts: cart refresh failed', err);
    }
  }

  // ─── UI helpers ─────────────────────────────────────────────────────────────

  function bindZoomControls() {
    document.querySelector('#zoomIn')?.addEventListener('click', () => zoom(1.25));
    document.querySelector('#zoomOut')?.addEventListener('click', () => zoom(0.75));
  }

  function zoom(factor) {
    if (!diagramImage) return;
    const w = diagramImage.getBoundingClientRect().width || diagramImage.naturalWidth;
    diagramImage.style.width = `${w * factor}px`;
  }

  function bindPartsRowHighlight() {
    document.addEventListener('mouseover', (e) => {
      const td = /** @type {HTMLElement} */(e.target).closest?.('tr.partrow td');
      td?.closest('tr')?.classList.add('highlight');
    });
    document.addEventListener('mouseout', (e) => {
      const td = /** @type {HTMLElement} */(e.target).closest?.('tr.partrow td');
      td?.closest('tr')?.classList.remove('highlight');
    });
  }

  // ─── Small utilities ────────────────────────────────────────────────────────

  function makeOption(value, label) {
    const o = document.createElement('option');
    o.value = value;
    o.textContent = label;
    return o;
  }

  function setLoading(selectEl) {
    selectEl.innerHTML = '';
    const o = makeOption('-1', 'Loading...');
    o.disabled = true;
    o.selected = true;
    selectEl.appendChild(o);
  }

  function show(elOrSel, displayValue) {
    const el = typeof elOrSel === 'string' ? document.querySelector(elOrSel) : elOrSel;
    if (!el) return;
    // Use an explicit value because the page CSS hides several of these by default
    // (display:none on the un-populated selects/containers); clearing the inline
    // style alone would let the CSS rule re-hide them.
    el.style.display = displayValue || (el.tagName === 'SELECT' ? 'inline-block' : 'block');
  }

  function hide(elOrSel) {
    const el = typeof elOrSel === 'string' ? document.querySelector(elOrSel) : elOrSel;
    if (el) el.style.display = 'none';
  }

  function hideAll(selectors) {
    for (const sel of selectors) hide(sel);
  }

  function withMargin(price) {
    if (price == null || price === '' || isNaN(parseFloat(price))) return null;
    return parseFloat(price) * PRICE_MARGIN;
  }

  function escapeHtml(s) {
    return String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttr(s) {
    return escapeHtml(s);
  }
})();
